import { useEffect, useRef, useState } from "react";
import { Form, Col, Row, Button } from "antd";
import usePost from "../../../global/hooks/usePost";
import useGet from "../../../global/hooks/useGet";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import DeviceIcon from "../../../assets/images/vectors/SideBarIcons/DeviceIcon";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { DuplicateMacModal } from "../../Device/Components/DuplicateMacModal";

/** MAC + 1 with octet carry — e.g. …:02:FF → …:03:00. Returns null if invalid. */
const incrementMac = (mac) => {
  const octets = String(mac ?? "")
    .split(":")
    .map((part) => parseInt(part, 16));
  if (octets.length !== 6 || octets.some((o) => !Number.isFinite(o))) {
    return null;
  }
  for (let i = 5; i >= 0; i -= 1) {
    octets[i] += 1;
    if (octets[i] <= 0xff) break;
    octets[i] = 0;
    if (i === 0) return null; // every octet rolled over
  }
  return octets
    .map((o) => o.toString(16).toUpperCase().padStart(2, "0"))
    .join(":");
};

/**
 * Add devices by count (Start MAC + Device Count).
 *
 * Can be used two ways:
 *  1. As a modal from ProductionDeviceForm info page   → props: productionId + itemId
 *  2. As a standalone page (via route)                → useParams: id + itemid
 */
export const DeviceProductionAddbyCount = ({
  isModalForm,
  onCancel,
  devicesRefetch,
  LicenseNo,
  // When used as modal from the info page, parent passes these:
  productionId: propProductionId,
  itemId: propItemId,
}) => {
  const [form] = Form.useForm();
  const { id: paramId, itemid: paramItemId } = useParams();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const autoFilledRef = useRef(null);

  // Resolve IDs — props take priority (modal usage), then fall back to URL params
  const resolvedProductionId = propProductionId || paramId;
  const resolvedItemId = propItemId || paramItemId;

  const postUrl = resolvedProductionId
    ? `${process.env.REACT_APP_API_URL}/api/device-production/${resolvedProductionId}/bulk-create-by-count/`
    : null;

  // Last MAC = the globally last-created device (device API) — Start MAC = last + 1.
  const lastDeviceUrl = `${process.env.REACT_APP_API_URL}/api/devices/?sort_by=DeviceNo&order=desc&limit=1`;
  const { data: lastDeviceData } = useGet(lastDeviceUrl);
  const lastMac = lastDeviceData?.data?.[0]?.Mac ?? null;

  const detailUrl =
    resolvedProductionId && resolvedItemId
      ? `${process.env.REACT_APP_API_URL}/api/device-production/${resolvedProductionId}/items/${resolvedItemId}/`
      : null;

  const { data } = useGet(detailUrl);
  const itemCount = data?.data?.ItemCount;
  const deviceCount = data?.data?.DeviceCount;
  const remaining =
    typeof itemCount === "number" && typeof deviceCount === "number"
      ? itemCount - deviceCount
      : null;

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const onFinish = async (values) => {
    const modValues = {
      ...values,
      production_item_id: resolvedItemId,
      LicenseNo,
    };
    try {
      await postAction(modValues);
    } catch {
      openErrorNotification("Something went wrong.");
    }
  };

  useEffect(() => {
    if (!lastMac) return;
    const current = form.getFieldValue("start_mac");
    if (
      current &&
      String(current).trim() !== "" &&
      String(current) !== String(autoFilledRef.current ?? "")
    ) {
      return;
    }
    const next = incrementMac(lastMac);
    if (!next) return;
    autoFilledRef.current = next;
    form.setFieldsValue({ start_mac: next });
  }, [form, lastMac]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      const details = postData?.data?.details || [];
      const hasDuplicates = details.some((item) => item?.status === 400);
      if (hasDuplicates) {
        setModal(details);
        devicesRefetch?.();
      } else {
        openConfirmNotification("Success");
        devicesRefetch?.();
        isModalForm ? onCancel?.() : navigate(-1);
      }
    }
  }, [postError, postData, postLoading]);

  const breadcrumbItems = [
    {
      label: "Device Production",
      href: `/device-production/info/${resolvedProductionId}`,
    },
    { label: "Add by Count", href: "#" },
  ];

  const buttonsData = [
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: () => form.submit(),
    },
    { label: "Cancel", icon: <CloseOutlined />, onClick: () => navigate(-1) },
  ];

  return (
    <>
      <Form
        validateTrigger="onChange"
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout="vertical"
        autoComplete="off"
      >
        {!isModalForm && (
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
        )}

        <div
          className={
            isModalForm ? "" : "bg-white overflow-hidden border rounded-[4px]"
          }
        >
          {!isModalForm && (
            <div className="flex items-center bg-gray-200 p-2.5">
              <DeviceIcon className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Device
              </span>
            </div>
          )}

          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Start MAC"
                  name="start_mac"
                  placeholder="e.g. 00:11:22:33:44:55"
                  required
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const mac = String(value).trim();
                        const valid =
                          /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(mac);
                        if (!valid) {
                          return Promise.reject(
                            new Error(
                              "Enter a valid MAC address (e.g. 00:11:22:33:44:55)",
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label={
                    remaining !== null
                      ? `Device Count (max ${remaining})`
                      : "Device Count"
                  }
                  name="device_count"
                  placeholder="Device Count"
                  type="number"
                  required
                  integerOnly
                  rules={[
                    { required: true, message: "Device Count is required" },
                    {
                      validator: async (_, value) => {
                        if (!value) return Promise.resolve();
                        const num = Number(value);
                        if (isNaN(num) || num <= 0)
                          return Promise.reject(
                            new Error("Device Count must be a positive number"),
                          );
                        if (remaining !== null && num > remaining)
                          return Promise.reject(
                            new Error(`Device Count must be ≤ ${remaining}`),
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Col>
            </Row>
          </div>
        </div>

        {isModalForm && (
          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
              onClick={() => form.submit()}
              loading={postLoading}
              disabled={postLoading}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button className="reset-button" onClick={onCancel}>
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        )}
      </Form>

      <DuplicateMacModal
        isModalOpen={modal !== null}
        setModal={setModal}
        modal={modal}
        isModalForm={isModalForm}
      />
    </>
  );
};
